'use client';

import { useFiltersContext } from '@components/FilterPage/Context/useFiltersContext';
import { Button, Drawer, DrawerBody, DrawerFooter } from '@coolblue-development/freddy';
import clearAllFiltersTrigger from '@coolblue-development/ga4-events/triggers/RecruitmentFilter/clearAllFiltersTrigger';
import closeFilterOverlayTrigger from '@coolblue-development/ga4-events/triggers/RecruitmentFilter/closeFilterOverlayTrigger';
import confirmFilterResultsTrigger from '@coolblue-development/ga4-events/triggers/RecruitmentFilter/confirmFilterResultsTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { useFreddyTranslations } from '@coolblue-development/next-design-system-utils/client';
import { useTranslation } from '@i18n/useTranslation';
import { Filters } from '@server/careers-backend/search';
import React, { useEffect, useState } from 'react';

import { FilterFacetsContent } from '../FilterFacetsContent';

type Props = {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  filters: Filters | undefined;
  resultCount?: number;
};

export const FilterFacetsModal = ({ isOpen, setIsOpen, filters, resultCount }: Props) => {
  const { t } = useTranslation('filterPage.filterFacets.drawer');
  const drawerTranslations = useFreddyTranslations('drawer');
  const { trackEvent } = useAnalytics();
  const { isPending, appliedFilters, setAllFilters, clearFilters } = useFiltersContext();

  const [initialFilterState, setInitialFilterState] = useState(appliedFilters);

  useEffect(() => {
    if (isOpen) {
      setInitialFilterState(appliedFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <Drawer
      translations={drawerTranslations}
      open={isOpen}
      onClose={() => {
        setAllFilters(initialFilterState);
        setIsOpen(false);
        trackEvent(closeFilterOverlayTrigger());
      }}
      placement='bottom'
      title={t('title')}
      size='medium'
      body={
        <DrawerBody>
          <FilterFacetsContent version='accordion' filters={filters} />
        </DrawerBody>
      }
      footer={
        <DrawerFooter>
          {Object.keys(appliedFilters).length > 0 && (
            <Button
              variant='tertiary'
              onClick={() => {
                clearFilters();
                setIsOpen(false);
                trackEvent(clearAllFiltersTrigger({ sub_feature: 'filter_overlay' }));
              }}
              fullWidth
            >
              {t('clearAll')}
            </Button>
          )}
          <Button
            onClick={() => {
              setIsOpen(false);
              trackEvent(confirmFilterResultsTrigger({ status: 'success' }));
            }}
            loading={isPending}
            fullWidth
          >
            {t('showVacancies', { count: resultCount })}
          </Button>
        </DrawerFooter>
      }
    />
  );
};
